/**
 * Tours específicos para la sección de Personas
 *
 * Estructura de rutas activas:
 *  /persons                              → MainPersonContent       (selector de roles)
 *  /persons/folders                      → FoldersMainView         (sistema de carpetas)
 *  /persons/folders/:folderId            → FolderDetailView        (detalle de carpeta)
 *  /persons/folders/unassigned/:roleId   → UnassignedUsersView     (sin asignar)
 *  /persons/folders/all/:roleId          → AllUsersView            (todos los usuarios)
 *  /persons/profile/:id                  → ProfileAdministration   (perfil de usuario)
 *  /persons/add/user/:id                 → RegisterAdministration  (registrar usuario)
 *  /persons/administrators               → ViewAdminContent
 *  /persons/operators                    → FoldersMainView (alias)
 *  /persons/ADOS                         → ViewOperationsAssistant
 *  /persons/owners                       → ViewOwners
 *  /persons/third-parties                → ViewThirdPartyContent
 *  /persons/RRHH                         → ViewRRHHContent
 *  /persons/setting/data                 → SettingPerson
 */

// ---------------------------------------------------------------------------
// 1. LAYOUT COMPARTIDO — header de Persons.jsx
// ---------------------------------------------------------------------------
export const getPersonsLayoutTour = (theme) => [
  {
    element: `.GM__${theme}-persons-title`,
    popover: {
      title: 'Título de la sección',
      description:
        'Indica la sección de Personas en la que te encuentras. Cambia dinámicamente según la ruta activa: Personas, Administradores, Operadores, Carpetas, etc.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-persons-navigation`,
    popover: {
      title: 'Ruta de navegación (Breadcrumb)',
      description:
        'Muestra la jerarquía de navegación desde la raíz. Haz clic en los segmentos anteriores para volver a cualquier nivel sin perder el contexto.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-btn-add-user`,
    popover: {
      title: 'Agregar usuario',
      description:
        'Abre un selector de roles para elegir el tipo de usuario a registrar (Operador, Administrador, Propietario, etc.) y luego inicia el formulario de registro.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.GM__${theme}-btn-settings`,
    popover: {
      title: 'Configuración de personas',
      description:
        'Accede a los catálogos maestros necesarios para crear usuarios: tipos de documentos requeridos, nacionalidades, parentescos y otras opciones de precreación.',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 2. DASHBOARD PRINCIPAL — /persons
// ---------------------------------------------------------------------------
export const getPersonsMainTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-person-container`,
    popover: {
      title: 'Panel principal de Personas',
      description:
        'Vista de inicio del módulo. Aquí se presentan los grupos de roles disponibles. Selecciona uno para ver y gestionar los usuarios de ese rol.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-person-grid`,
    popover: {
      title: 'Grupos de roles',
      description:
        'Cada tarjeta representa un rol del sistema: Operadores, Administradores, Propietarios, RRHH, Terceros, Auxiliares de Operaciones, etc. Haz clic en cualquiera para navegar a la lista de usuarios de ese rol.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: `.GM__${theme}-grid-item`,
    popover: {
      title: 'Tarjeta de rol',
      description:
        'Muestra el nombre del rol, un ícono representativo y la cantidad total de usuarios activos. Al hacer clic navega directamente a la gestión de ese rol.',
      side: 'right',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 3. SISTEMA DE CARPETAS — /persons/folders  (y /persons/operators)
// ---------------------------------------------------------------------------
export const getPersonsFoldersTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-folders-container`,
    popover: {
      title: 'Sistema de carpetas',
      description:
        'Los usuarios se organizan en carpetas para facilitar su gestión por grupos, rutas u operaciones. Puedes crear nuevas carpetas, reorganizarlas y asignar usuarios a cada una.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folders-section`,
    popover: {
      title: 'Sección de carpetas',
      description:
        'Las carpetas se agrupan en secciones. Cada sección puede expandirse o contraerse. El botón lateral permite colapsar toda la sección para liberar espacio visual.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folders-section-header`,
    popover: {
      title: 'Encabezado de sección',
      description:
        'Muestra el nombre de la sección y un contador de carpetas que contiene. Haz clic en el ícono de flecha para expandir o contraer.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folders-grid`,
    popover: {
      title: 'Grid de carpetas',
      description:
        'Cada elemento del grid es una carpeta. Puedes reordenarlas arrastrando (drag & drop). Haz clic en una carpeta para ver los usuarios que contiene.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folders-card`,
    popover: {
      title: 'Tarjeta de carpeta',
      description:
        'Muestra el nombre de la carpeta, su ícono personalizado y el número de usuarios asignados. Al hacer clic accedes al detalle con el listado completo de usuarios.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folders-new-card`,
    popover: {
      title: 'Crear nueva carpeta',
      description:
        'Agrega una nueva carpeta a la sección actual. Se abrirá un formulario para configurar el nombre, ícono y color de la carpeta.',
      side: 'right',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 4. DETALLE DE CARPETA — /persons/folders/:folderId
// ---------------------------------------------------------------------------
export const getPersonsFolderDetailTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-folder-container`,
    popover: {
      title: 'Detalle de la carpeta',
      description:
        'Vista completa de los usuarios asignados a esta carpeta. Puedes buscar, filtrar, cambiar la vista entre grid/lista y gestionar asignaciones directamente desde aquí.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folder-header`,
    popover: {
      title: 'Encabezado de la carpeta',
      description:
        'Muestra el nombre de la carpeta, los controles de búsqueda y las acciones disponibles según tu rol.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folder-search-compact`,
    popover: {
      title: 'Buscar usuarios',
      description:
        'Filtra los usuarios de la carpeta por nombre, cédula u otros datos. La búsqueda es en tiempo real.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-folder-header-controls`,
    popover: {
      title: 'Controles de vista',
      description:
        'Cambia entre vista de grilla (cards) y vista de lista (tabla). También incluye el botón para asignar usuarios existentes a esta carpeta.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.GM__${theme}-folder-btn-assign`,
    popover: {
      title: 'Asignar usuario a la carpeta',
      description:
        'Abre un buscador para asignar un usuario ya registrado a esta carpeta. Los usuarios no asignados a ninguna carpeta aparecen en la vista "Sin Asignar".',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 5. TODOS LOS USUARIOS — /persons/folders/all/:roleId
// ---------------------------------------------------------------------------
export const getPersonsAllUsersTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-users-container`,
    popover: {
      title: 'Todos los usuarios del rol',
      description:
        'Vista completa de todos los usuarios del rol seleccionado, independientemente de si están asignados a una carpeta o no. Útil para búsquedas globales y administración masiva.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-users-header`,
    popover: {
      title: 'Encabezado y controles',
      description:
        'Contiene la búsqueda principal, los filtros activos y las acciones de vista. Puedes alternar estadísticas de resumen y cambiar entre grilla y lista.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-users-search-compact`,
    popover: {
      title: 'Búsqueda de usuarios',
      description:
        'Ingresa nombre, cédula o correo para encontrar un usuario específico dentro del rol. La búsqueda filtra en tiempo real.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-users-active-filters`,
    popover: {
      title: 'Filtros activos',
      description:
        'Muestra los filtros aplicados actualmente (por carpeta, estado, etc.). Haz clic en la X de cada etiqueta para quitar ese filtro individual.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-users-content`,
    popover: {
      title: 'Listado de usuarios',
      description:
        'Todos los usuarios del rol según los filtros activos. Haz clic en cualquier usuario para ver su perfil completo.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 6. LISTA POR ROL — /persons/administrators|ADOS|owners|third-parties|RRHH
//    Estructura compartida: ViewAdminContent, ViewOperationsAssistant,
//    ViewOwners, ViewThirdPartyContent, ViewRRHHContent
// ---------------------------------------------------------------------------
export const getPersonsRoleListTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-operator-container`,
    popover: {
      title: 'Listado de usuarios por rol',
      description:
        'Lista todos los usuarios registrados bajo este rol específico. Puedes buscar, filtrar y acceder al perfil individual de cada uno.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-operator-header`,
    popover: {
      title: 'Barra de búsqueda y acciones',
      description:
        'Busca usuarios por nombre o cédula. Las acciones rápidas permiten agregar un nuevo usuario directamente al rol actual.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-search-container`,
    popover: {
      title: 'Búsqueda',
      description:
        'Escribe el nombre, cédula u otro dato del usuario para encontrarlo en la lista. La búsqueda filtra en tiempo real mientras escribes.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-operator-grid`,
    popover: {
      title: 'Grid de usuarios',
      description:
        'Tarjetas de todos los usuarios del rol. Cada tarjeta muestra nombre, foto y datos clave. Haz clic en una tarjeta para ver el perfil completo.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-pagination-container`,
    popover: {
      title: 'Paginación',
      description:
        'Navega entre páginas de resultados cuando hay más usuarios de los que caben en pantalla.',
      side: 'top',
      align: 'center',
    },
  },
];

// ---------------------------------------------------------------------------
// 7. PERFIL DE USUARIO — /persons/profile/:id  (ProfileAdministration)
//    Factory con interacción: auto-expand, pregunta edit mode y docs detallados
// ---------------------------------------------------------------------------

/**
 * @param {string}  theme        — tema actual ("dark" | "light")
 * @param {object}  driverRef    — ref cuyo .current = instancia driver.js
 * @param {boolean} canEdit      — ¿el usuario puede editar este perfil?
 */
export const createPersonProfileTour = (theme, driverRef, canEdit = true) => {
  const getDriver = () => driverRef.current;

  // Expande la sección de información del perfil si está cerrada
  const ensureProfileExpanded = () => {
    const section = document.querySelector(`.PA__${theme}-user-section`);
    if (section && !section.classList.contains('expanded')) {
      const btn = section.querySelector(`.PA__${theme}-user-expand-button`);
      btn?.click();
    }
  };

  // Colapsa la sección de información del perfil si está abierta
  const ensureProfileCollapsed = () => {
    const section = document.querySelector(`.PA__${theme}-user-section`);
    if (section && section.classList.contains('expanded')) {
      const btn = section.querySelector(`.PA__${theme}-user-expand-button`);
      btn?.click();
    }
  };

  // ── A. Pasos de layout ──────────────────────────────────────────────────
  const layoutSteps = getPersonsLayoutTour(theme);

  // ── B. Pasos de información EN MODO LECTURA ─────────────────────────────
  const infoReadSteps = [
    {
      element: `.PA__${theme}-main-container`,
      popover: {
        title: 'Perfil completo del usuario',
        description:
          'La vista se divide en dos grandes zonas: arriba los datos del usuario y abajo la gestión documental. Recorreremos ambas detalladamente.',
        side: 'top',
        align: 'start',
      },
    },
    {
      // Se expande automáticamente al llegar aquí
      element: `.PA__${theme}-user-section`,
      onHighlightStarted: () => ensureProfileExpanded(),
      popover: {
        title: 'Información del usuario — abriendo sección',
        description:
          'Esta sección contiene todos los datos del usuario. Se está expandiendo automáticamente para mostrarte el detalle completo.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: `.PA__${theme}-user-expand-button`,
      popover: {
        title: 'Expandir / Contraer',
        description:
          'Permite ampliar o reducir esta sección. Contraída muestra solo los datos esenciales; expandida muestra todos los grupos de campos: personales, contacto, laborales y sistema.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: `.IP__${theme}-photos-section`,
      popover: {
        title: 'Foto de perfil',
        description:
          'Imagen principal del usuario. Puede mostrar también la foto del documento de identidad (cédula u otro) como referencia secundaria.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: `.IP__${theme}-identity-row`,
      popover: {
        title: 'Nombre completo',
        description:
          'Nombre y apellidos del usuario tal como están registrados en el sistema. En modo edición estos campos son editables de forma directa.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: `.IP__${theme}-chips-row`,
      popover: {
        title: 'Atributos clave (modo lectura)',
        description:
          'Vista rápida de los estados del perfil: Activo (si el usuario puede operar), Propietario (si es dueño de vehículo) y Es Empresa. También muestra el rol asignado.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: `.IP__${theme}-user-expanded-content`,
      popover: {
        title: 'Datos detallados del usuario',
        description:
          'Sección expandida con toda la información distribuida en dos columnas. Columna izquierda: datos personales y del sistema. Columna derecha: datos de contacto, emergencia y salud.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: `.IP__${theme}-column-left`,
      popover: {
        title: 'Datos personales y del sistema',
        description:
          'Tipo y número de documento, fecha de vencimiento del documento, fecha de nacimiento, cargo, grupo sanguíneo, departamento/municipio de residencia y rol en el sistema.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: `.IP__${theme}-column-right`,
      popover: {
        title: 'Contacto y salud',
        description:
          'Teléfonos (con indicativo de país), correo electrónico, contacto de emergencia (nombre y teléfono), EPS (entidad prestadora de salud) y ARL (aseguradora de riesgos laborales).',
        side: 'left',
        align: 'start',
      },
    },
  ];

  // ── C. Pasos de modo EDICIÓN ────────────────────────────────────────────
  const editModeSteps = canEdit
    ? [
        {
          // Re-expand al entrar en modo edición
          element: `.PA__${theme}-user-section`,
          onHighlightStarted: () => ensureProfileExpanded(),
          popover: {
            title: 'Modo edición — activo',
            description:
              'El perfil ahora está en modo edición. Los campos son interactivos. La sección se expande para que puedas ver y modificar cada dato.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: `.IP__${theme}-identity-row`,
          popover: {
            title: 'Editar: Nombre completo',
            description:
              'Los tres campos de nombre son editables directamente: nombre, primer apellido y segundo apellido. Asegúrate de usar el nombre oficial del documento de identidad.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: `.IP__${theme}-chips-row`,
          popover: {
            title: 'Editar: Estados y rol',
            description:
              'Los chips se convierten en controles interactivos. Los interruptores "Activo", "Propietario" y "Es Empresa" habilitan o deshabilitan esa condición. El selector de rol permite cambiar el rol asignado en el sistema.',
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: `.IP__${theme}-user-expanded-content`,
          popover: {
            title: 'Editar: Datos completos',
            description:
              'Todos los campos del usuario están disponibles para su edición. Recorreremos cada columna a continuación.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: `.IP__${theme}-column-left`,
          popover: {
            title: 'Editar: Datos personales y del sistema',
            description:
              'Tipo de documento, número, fecha de vencimiento, fecha de nacimiento, cargo, departamento/municipio, grupo sanguíneo y rol del sistema. Estos datos son clave para la operación y la nómina.',
            side: 'right',
            align: 'start',
          },
        },
        {
          element: `.IP__${theme}-column-right`,
          popover: {
            title: 'Editar: Contacto y salud',
            description:
              'Teléfonos con código de país, correo electrónico, nombre y teléfono del contacto de emergencia, EPS y ARL. Mantén estos datos actualizados: son críticos para emergencias.',
            side: 'left',
            align: 'start',
          },
        },
      ]
    : [];

  // ── D. Pasos de DOCUMENTOS ──────────────────────────────────────────────
  const docsSteps = [
    {
      // Colapsa el perfil al llegar a documentos (viene del flujo de edición)
      element: `.PA__${theme}-filter-search`,
      onHighlightStarted: () => ensureProfileCollapsed(),
      popover: {
        title: 'Filtrar documentos',
        description:
          'Escribe el nombre o tipo de documento para encontrarlo rápidamente. La búsqueda filtra en tiempo real tanto los documentos cargados como los pendientes de carga.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: `.PA__${theme}-filter-actions-desktop`,
      popover: {
        title: 'Acciones sobre documentos',
        description: canEdit
          ? 'Botones disponibles: filtrar documentos excedentes, descargar múltiples archivos de una vez y ver la papelera de documentos eliminados.'
          : 'Puedes descargar múltiples documentos a la vez. La edición y carga de documentos no está disponible para tu rol actual.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: `.PA__${theme}-documents-container`,
      popover: {
        title: 'Zona de documentos',
        description:
          'Todas las secciones documentales del usuario aparecen aquí. Se dividen en categorías según el tipo de documento requerido para el rol.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: `.PA__${theme}-document-section`,
      popover: {
        title: 'Sección de documentos',
        description:
          'Cada sección tiene un botón de expandir/contraer independiente. Expandida, muestra la tabla completa con todos los documentos de esa categoría y sus acciones.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: `.DS__${theme}-table`,
      popover: {
        title: 'Tabla de documentos',
        description:
          'Columnas: Estado (indicador de color), Nombre del documento, Fecha de vencimiento y Acciones. Los documentos sin archivo aparecen como "pendientes de carga".',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: `.DS__${theme}-table-row`,
      popover: {
        title: 'Fila de documento',
        description:
          'Cada fila es un tipo de documento requerido. El indicador de estado usa colores: 🟢 Vigente, 🟡 Próximo a vencer (menos de 30 días), 🔴 Vencido. Si no hay archivo cargado, aparece como pendiente.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: `.DS__${theme}-actions-container`,
      popover: {
        title: 'Acciones del documento',
        description: canEdit
          ? '👁 Previsualizar el archivo sin descargarlo. ⬆ Subir un archivo nuevo o reemplazar el existente. 🗑 Eliminar el documento (irá a la papelera y podrá recuperarse).'
          : '👁 Previsualizar el archivo del documento. La edición y carga de documentos no está disponible para tu rol actual.',
        side: 'left',
        align: 'center',
      },
    },
  ];

  // ── Calcular índices para saltos del step de decisión ───────────────────
  const editStartIdx = layoutSteps.length + infoReadSteps.length + 1; // +1 por el step de decisión
  const docsStartIdx = editStartIdx + editModeSteps.length;

  // ── E. Paso de DECISIÓN (¿explorar edición?) ────────────────────────────
  const decisionStep = canEdit
    ? [
        {
          element: `.PA__${theme}-user-section`,
          onHighlighted: () => {
            setTimeout(() => {
              document
                .getElementById('tour-btn-yes-edit-person')
                ?.addEventListener('click', () => {
                  const editBtn = document.querySelector('[data-label="Editar Perfil"]');
                  if (editBtn) {
                    editBtn.click();
                    setTimeout(() => getDriver()?.moveTo(editStartIdx), 600);
                  } else {
                    getDriver()?.moveTo(docsStartIdx);
                  }
                });

              document
                .getElementById('tour-btn-no-edit-person')
                ?.addEventListener('click', () => {
                  ensureProfileCollapsed();
                  getDriver()?.moveTo(docsStartIdx);
                });
            }, 50);
          },
          popover: {
            title: '¿Explorar el modo de edición?',
            description: `<p style="margin:0 0 12px 0;line-height:1.55">Has visto todos los datos en modo lectura. ¿Deseas explorar cómo funciona la edición de cada campo del usuario?</p><div style="display:flex;gap:8px"><button id="tour-btn-yes-edit-person" style="padding:7px 18px;background:#3b82f6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600">Sí, explorar edición</button><button id="tour-btn-no-edit-person" style="padding:7px 18px;background:transparent;border:1px solid #9ca3af;border-radius:6px;cursor:pointer;font-size:13px;color:inherit">Ir a documentos</button></div>`,
            showButtons: ['close'],
            side: 'bottom',
            align: 'center',
          },
        },
      ]
    : [];

  // ── Componer el tour completo ────────────────────────────────────────────
  return [
    ...layoutSteps,
    ...infoReadSteps,
    ...decisionStep,
    ...editModeSteps,
    ...docsSteps,
  ];
};

// ---------------------------------------------------------------------------
// 8. REGISTRAR USUARIO — /persons/add/user/:id  (RegisterAdministration)
// ---------------------------------------------------------------------------
export const getPersonsRegisterTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.RA__${theme}-main-container`,
    popover: {
      title: 'Formulario de registro de usuario',
      description:
        'Aquí registras un nuevo usuario seleccionando el rol asignado. El formulario está organizado en secciones expandibles para facilitar el ingreso de toda la información.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.RA__${theme}-user-section`,
    popover: {
      title: 'Datos del usuario',
      description:
        'Sección principal del formulario con los datos personales y laborales. Expándela para ver y completar todos los campos: nombre, cédula, contacto, cargo y más.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.RA__${theme}-user-expand-button`,
    popover: {
      title: 'Expandir el formulario',
      description:
        'Amplía la vista para mostrar todos los campos del usuario. Asegúrate de completar los campos obligatorios antes de guardar.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: `.RA__${theme}-user-content`,
    popover: {
      title: 'Campos del formulario',
      description:
        'Datos personales, de contacto y laborales. Los campos marcados con asterisco (*) son obligatorios. Algunos campos como el rol ya vienen preasignados según la selección inicial.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-btn-save-user`,
    popover: {
      title: 'Guardar usuario',
      description:
        'Guarda el nuevo usuario con todos los datos ingresados. El sistema validará los campos obligatorios antes de confirmar el registro.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.RA__${theme}-documents-container`,
    popover: {
      title: 'Documentos del usuario',
      description:
        'Al registrar el usuario, puedes adjuntar los documentos requeridos de inmediato desde esta sección, o hacerlo más tarde desde el perfil.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 9. CONFIGURACIÓN — /persons/setting/data  (SettingPerson)
// ---------------------------------------------------------------------------
export const getPersonsSettingTour = (theme) => [
  ...getPersonsLayoutTour(theme),
  {
    element: `.GM__${theme}-dashboard-person`,
    popover: {
      title: 'Configuración de personas',
      description:
        'Panel de configuración con los catálogos maestros del módulo de personas. Aquí defines los datos de precreación necesarios para registrar usuarios.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-settings-tabs`,
    popover: {
      title: 'Categorías de configuración',
      description:
        'Cada pestaña corresponde a un catálogo diferente: tipos de documentos, nacionalidades, cargos laborales, etc. Navega entre ellas para gestionar cada uno.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.GM__${theme}-tab-navigation-container`,
    popover: {
      title: 'Navegación de pestañas',
      description:
        'Usa las flechas o el indicador de páginas para desplazarte cuando hay más pestañas de las que caben en pantalla.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: `.GM__${theme}-tab-content`,
    popover: {
      title: 'Contenido del catálogo',
      description:
        'Aquí creas, editas o eliminas los registros del catálogo seleccionado. Los cambios se reflejan de inmediato en los formularios de registro y edición de usuarios.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// SELECTOR — elige el tour según la ruta actual de /persons
// ---------------------------------------------------------------------------
export const getPersonsTourByPath = (theme, pathname) => {
  // Registro de usuario
  if (pathname.includes('/persons/add/user/')) return getPersonsRegisterTour(theme);

  // Configuración
  if (pathname.includes('/persons/setting')) return getPersonsSettingTour(theme);

  // Todos los usuarios (all/:roleId)
  if (pathname.includes('/persons/folders/all/')) return getPersonsAllUsersTour(theme);

  // Sin asignar o gestionar — tour genérico de folders
  if (pathname.includes('/persons/folders/unassigned/') || pathname.includes('/persons/folders/manage/')) {
    return getPersonsFoldersTour(theme);
  }

  // Detalle de carpeta específica (/persons/folders/:folderId)
  if (/\/persons\/folders\/\d+/.test(pathname)) return getPersonsFolderDetailTour(theme);

  // Sistema de carpetas raíz
  if (pathname.includes('/persons/folders') || pathname === '/persons/operators') {
    return getPersonsFoldersTour(theme);
  }

  // Listas por rol
  if (
    pathname.includes('/persons/administrators') ||
    pathname.includes('/persons/ADOS') ||
    pathname.includes('/persons/owners') ||
    pathname.includes('/persons/third-parties') ||
    pathname.includes('/persons/RRHH')
  ) {
    return getPersonsRoleListTour(theme);
  }

  // Dashboard principal
  if (pathname === '/persons') return getPersonsMainTour(theme);

  // Fallback: solo layout
  return getPersonsLayoutTour(theme);
};
